import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  X, Check, CheckCheck, ExternalLink, CircleDot, Hourglass, Map as MapIcon, FileText, UserRound,
  UserPlus, UserMinus, PlusCircle, Type, Lock, Ban,
} from 'lucide-react';
import { useT } from '../../i18n';
import { fullDay, plannedLabel } from './dates';
import { timelineApi, type ChangeType, type TaskChangeEntry, type TimelineTask } from './api';
import { taskStatusName } from '../../utils/taskStatusName';

// [Proposition: timeline-taches] Right-hand panel listing every change made to
// a task, newest first. Changes not validated yet come first, on an orange
// edge, each with its own "Validate" (plus "Validate all"); the PM validates
// them in the meeting, which clears the task's orange button.

const ICON: Record<ChangeType, ReactNode> = {
  created: <PlusCircle size={14} />,
  title: <Type size={14} />,
  description: <FileText size={14} />,
  status: <CircleDot size={14} />,
  blocked_reason: <Ban size={14} />,
  due_date: <Hourglass size={14} />,
  planned_at: <MapIcon size={14} />,
  assignee: <UserRound size={14} />,
  collaborator_added: <UserPlus size={14} />,
  collaborator_removed: <UserMinus size={14} />,
  privacy: <Lock size={14} />,
};

/** When a change was made: "8 oct. 2026 · 15:15". */
function formatAt(iso: string, t: (key: string) => string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${fullDay(iso, t)} · ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ChangeBody({ c }: { c: TaskChangeEntry }) {
  const { t } = useT();
  const none = t('tlt.log.none');
  const value = (v: string | null) => {
    if (!v) return none;
    if (c.change_type === 'due_date') return fullDay(v, t);
    if (c.change_type === 'planned_at') return plannedLabel(v, t);
    if (c.change_type === 'privacy') return t(v === 'private' ? 'tlt.log.private' : 'tlt.log.public');
    if (c.change_type === 'status') return taskStatusName(v, t);
    return v;
  };
  const postponed = (c.change_type === 'due_date' || c.change_type === 'planned_at')
    && !!c.old_value && !!c.new_value && c.new_value > c.old_value;

  switch (c.change_type) {
    case 'created':
      return <div className="tlt-log-what">{t('tlt.log.created')}</div>;
    case 'collaborator_added':
      return <div className="tlt-log-what">{t('tlt.log.collaboratorAdded', { name: c.new_value || '' })}</div>;
    case 'collaborator_removed':
      return <div className="tlt-log-what">{t('tlt.log.collaboratorRemoved', { name: c.old_value || '' })}</div>;
    case 'description':
      return (
        <>
          <div className="tlt-log-what">{t('tlt.log.description')}</div>
          {c.old_value && <div className="tlt-log-text is-old">{c.old_value}</div>}
          <div className="tlt-log-text">{c.new_value || none}</div>
        </>
      );
    default:
      return (
        <div className="tlt-log-what">
          <span className="tlt-log-field">{t(`tlt.log.${c.change_type}`)}</span>
          <span className="tlt-log-old">{value(c.old_value)}</span>
          <span className="tlt-log-arrow">→</span>
          <strong>{value(c.new_value)}</strong>
          {postponed && <span className="tlt-log-tag">{t('tlt.postponed')}</span>}
        </div>
      );
  }
}

export function ChangeLogPanel({ task, onClose, onValidated }: {
  task: TimelineTask;
  onClose: () => void;
  onValidated: () => void;
}) {
  const { t } = useT();
  const [changes, setChanges] = useState<TaskChangeEntry[] | null>(null);
  const [canValidate, setCanValidate] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    timelineApi.getChanges(task.id)
      .then((r) => { setChanges(r.changes); setCanValidate(r.canValidate); })
      .catch((e: Error) => alert(e.message));
  }, [task.id]);
  useEffect(() => { setChanges(null); load(); }, [load]);

  const pending = (changes ?? []).filter(c => !c.validated_at);
  const validated = (changes ?? []).filter(c => c.validated_at);

  const validate = (ids: number[]) => {
    if (ids.length === 0) return;
    setSaving(true);
    timelineApi.validate(task.id, ids)
      .then(() => { load(); onValidated(); })
      .catch((e: Error) => alert(e.message))
      .finally(() => setSaving(false));
  };

  return (
    <div className="panel-overlay open" onClick={onClose}>
      <div className="panel-overlay-bg" />
      <div className="panel-slide tlt-log" onClick={(e) => e.stopPropagation()}>
        <div className="panel-slide-head">
          <div style={{ minWidth: 0 }}>
            <div className="tlt-log-kicker">{t('tlt.log.kicker')}</div>
            <div className="panel-slide-title">{task.title}</div>
            <div className="tlt-log-sub">
              {taskStatusName(task.column_label, t)} · {task.assignee_name || t('vjt.task.unassigned')}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 'var(--s8)', alignItems: 'center', flexShrink: 0 }}>
            {task.can_open && (
              <a className="btn btn-ghost btn-sm" href={`#tasks?task=${task.id}`} title={t('vjt.task.open')}><ExternalLink size={14} /></a>
            )}
            <button className="panel-slide-close" onClick={onClose}><X size={16} /></button>
          </div>
        </div>
        <div className="panel-slide-body">
          {changes == null ? <div className="spinner" /> : (
            <>
              <div className="tlt-log-section">
                <span className={`tlt-log-section-title${pending.length > 0 ? ' is-pending' : ''}`}>
                  {t('tlt.log.pendingTitle', { n: pending.length })}
                </span>
                {canValidate && pending.length > 1 && (
                  <button type="button" className="btn btn-accent btn-sm" disabled={saving} onClick={() => validate(pending.map(c => c.id))}>
                    <CheckCheck size={13} /> {t('tlt.log.validateAll')}
                  </button>
                )}
              </div>
              {pending.length === 0 && <p className="tlt-log-empty">{t('tlt.log.nothingPending')}</p>}
              {!canValidate && pending.length > 0 && <p className="tlt-log-empty">{t('tlt.log.pmOnly')}</p>}
              <ul className="tlt-log-list">
                {pending.map(c => (
                  <li key={c.id} className="tlt-log-item is-pending">
                    <span className="tlt-log-icon">{ICON[c.change_type]}</span>
                    <div className="tlt-log-body">
                      <ChangeBody c={c} />
                      <div className="tlt-log-meta">{c.actor_name || '—'} · {formatAt(c.changed_at, t)}</div>
                    </div>
                    {canValidate && (
                      <button type="button" className="btn btn-ghost btn-sm tlt-log-ok" disabled={saving} onClick={() => validate([c.id])}>
                        <Check size={13} /> {t('tlt.log.validateOne')}
                      </button>
                    )}
                  </li>
                ))}
              </ul>

              {validated.length > 0 && (
                <>
                  <div className="tlt-log-section">
                    <span className="tlt-log-section-title">{t('tlt.log.validatedTitle')}</span>
                  </div>
                  <ul className="tlt-log-list">
                    {validated.map(c => (
                      <li key={c.id} className="tlt-log-item">
                        <span className="tlt-log-icon">{ICON[c.change_type]}</span>
                        <div className="tlt-log-body">
                          <ChangeBody c={c} />
                          <div className="tlt-log-meta">
                            {c.actor_name || '—'} · {formatAt(c.changed_at, t)}
                            {c.validated_by === 'reprise'
                              ? <> · {t('tlt.log.takenOver')}</>
                              : c.validated_by && <> · {t('tlt.log.validatedBy', { who: c.validated_by, date: formatAt(c.validated_at!, t) })}</>}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
